import React from 'react';
import { Check, Lock } from 'lucide-react';
import { IntegrationLogo } from '../IntegrationLogo';
import { EXPORT_LABEL, EXPORT_META, EXPORT_TITLE, ExportPanel, PanelHeader } from './exportUi';

/**
 * "BUILT FOR TEAMS", AS PRODUCT UI — AN EXPORT-ONLY ASSET.
 *
 * WHY THIS EXISTS. The home page's "Built for teams" card carries an
 * illustration of a group of people. That works as decoration inside a
 * marketing board, but the export page is an asset library: its users are
 * teammates and developers pulling frames into videos, decks and docs. An
 * illustration is the one asset nobody can use that way — you cannot point at
 * it and say "this is the thing". Every other frame on that page is real
 * product UI, so this is the odd one out.
 *
 * So this is the same claim — "shared access, workflows and company context in
 * one place, not a drawer of personal accounts" — stated as the screen that
 * proves it: a group, the people in it, the access each one has, and the
 * connections the whole group reaches through. The last row is the argument:
 * the tools belong to the GROUP, so nobody is holding them in a personal
 * login.
 *
 * It deliberately does NOT replace the home page card. The two are allowed to
 * differ: the page is selling a benefit, the asset is showing a product.
 *
 * Fully static by design — no hover, no entrance, no interval. An asset that
 * only looks right after an animation has run is an asset that exports wrong.
 */

/** One group, six people. Access is a real distinction in the product — an
 * owner administers the group, members use what it holds, and a viewer can see
 * outputs without being able to run anything. Showing all three is what makes
 * this read as governance rather than a contact list. */
const MEMBERS: {
  name: string;
  role: string;
  access: 'Owner' | 'Member' | 'Viewer';
  initials: string;
}[] = [{
  name: 'Sarah Chen',
  role: 'VP Revenue',
  access: 'Owner',
  initials: 'SC'
}, {
  name: 'Marcus Webb',
  role: 'Account Executive',
  access: 'Member',
  initials: 'MW'
}, {
  name: 'Priya Raman',
  role: 'Sales Engineer',
  access: 'Member',
  initials: 'PR'
}, {
  name: 'Tom Alvarez',
  role: 'RevOps',
  access: 'Member',
  initials: 'TA'
}, {
  name: 'Dana Okafor',
  role: 'Finance',
  access: 'Viewer',
  initials: 'DO'
}];

/** What the group reaches. Named tools rather than a count: "4 connections"
 * states a quantity, four logos state a fact the reader can check. */
const GROUP_CONNECTIONS: [string, string][] = [['salesforce', 'Salesforce'], ['slack', 'Slack'], ['gmail', 'Gmail'], ['notion', 'Notion']];

/** Owner gets the accent, viewer gets the quietest treatment — the chip is a
 * scanning aid, so the three levels must be distinguishable at a glance rather
 * than three shades of the same warm grey. */
const ACCESS_STYLE: Record<string, string> = {
  Owner: 'bg-[#FDF0E6] text-[#B4470F]',
  Member: 'bg-[#F1EAE0] text-[#6B5D52]',
  Viewer: 'border border-[#EFE7DA] text-[#8A7B6D]'
};
export function SharedAccessPanel() {
  return <ExportPanel data-id="element-3416" data-mp="12u97n7lp6esx">
      {/* Header on the shared export scale, so this panel and the skills,
       time-returned and agent assets cannot be told apart as a set. The state
       is stated, not offered — a call to action inside a still is an
       instruction nobody can follow. */}
      <PanelHeader label="Group" title="Revenue team" meta="5 people · 12 agents · 4 connections" aside={<span className="inline-flex items-center gap-1.5 rounded-full bg-[#F1EAE0] px-3 py-1.5 text-[12px] font-bold text-[#6B5D52]" data-id="element-3418" data-mp="1lrae6wuwgmyz">
          <Check size={12} strokeWidth={3} className="text-[#16A34A]" data-id="element-3419" data-mp="1gu1xl0pegqzu" />
          Shared with the group
        </span>} data-id="element-3417" data-mp="p344vwn196ed" />

      {/* People and their access. */}
      <ul className="px-6" data-id="element-3420" data-mp="17ritkd9vo8c0">
        {MEMBERS.map(member => <li key={member.name} className="flex items-center gap-3 border-b border-[#F1EAE0] py-3.5 last:border-0" data-id="element-3421" data-mp="17uwvxlijkhjg">
            <span aria-hidden="true" className={`flex size-8 flex-none items-center justify-center rounded-full text-[11.5px] font-bold ${member.access === 'Owner' ? 'bg-[#EA580C] text-white' : 'bg-[#F1EAE0] text-[#6B5D52]'}`} data-id="element-3422" data-mp="3sj2tc4lurm8s">
              {member.initials}
            </span>
            <span className="min-w-0 flex-1" data-id="element-3423" data-mp="1p2o5elush34r">
              <span className={`${EXPORT_TITLE} block truncate`} data-id="element-3424" data-mp="1ba801eef0t0o">{member.name}</span>
              <span className={`${EXPORT_META} block truncate`} data-id="element-3425" data-mp="1faifris0exsq">{member.role}</span>
            </span>
            <span className={`flex-none rounded-full px-2.5 py-1 text-[11.5px] font-bold ${ACCESS_STYLE[member.access]}`} data-id="element-3426" data-mp="2302vlssv9exs">
              {member.access}
            </span>
          </li>)}
      </ul>

      {/* THE POINT OF THE WHOLE PANEL. Access follows the group, so the
       connections sit under the group rather than under a person. The padlock
       says these are held centrally — it is the visual difference between this
       and "a drawer of personal accounts". */}
      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-[#EFE7DA] bg-[#FAF8F4] px-6 py-4" data-id="element-3427" data-mp="njufsa22nygn">
        <p className={`${EXPORT_LABEL} flex items-center gap-2`} data-id="element-3428" data-mp="f227gpg507hw">
          <Lock size={12} strokeWidth={2.4} data-id="element-3429" data-mp="f4ob4dzwq2uu" />
          Company connections
        </p>
        <div className="flex flex-wrap items-center gap-2" data-id="element-3430" data-mp="b3b6g78jw5pi">
          {GROUP_CONNECTIONS.map(([slug, name]) => <span key={slug} className="flex items-center gap-2 rounded-full border border-[#EFE7DA] bg-white py-1 pl-1 pr-3" data-id="element-3431" data-mp="oyx1av1gtjpu">
              <IntegrationLogo slug={slug} name={name} className="size-[18px]" rounded="rounded-full" data-id="element-3432" data-mp="3shgkl8261f8i" />
              <span className="text-[12px] font-semibold text-[#2B211A]" data-id="element-3433" data-mp="3nj5dh5399itz">{name}</span>
            </span>)}
        </div>
      </div>
    </ExportPanel>;
}